'use client';
import React, { useState, useEffect, useMemo } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { DialogHeader } from '@src/v3/components/coreUI/dialog';
import Button from '@src/v3/components/coreUI/button';
import TextField from '@src/v3/components/coreUI/textField';
import Icon from '@src/v3/components/coreUI/icon';
import GradientPanelSvg from '@src/v3/assets/svg/gradientPanel';
import UserProfileCard from './UserProfileCard';
import {
  RegisterStepProps,
  RegisterFormValues,
  UserType,
} from '@src/v3/interfaces/components/registerModal';
import { userDetailsValidationSchema } from '@src/v3/schemas/components/register/index';
import Link from 'next/link';
import { Checkbox } from '@src/v3/components/coreUI/checkbox';
import { APP_ROUTE } from '@src/v3/constants/routes';
import { ZEME_USERS_ROLE } from '@src/v3/constants/configs';
import { checkPhoneExist } from '@src/v3/services/auth';
import {
  buildE164PhoneNumber,
  getIso2FromDialCode,
  getPhoneMask,
  formatPhoneDisplayForCountry,
} from '@src/v3/utils/helper';
import { ERROR_MESSAGES } from '@src/v3/constants/messages';
import CountryCodeSelect from '@src/v3/components/coreUI/countryCodeSelect';
import {
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';

const UserDetailsStep: React.FC<RegisterStepProps> = ({
  onUserDetailsSubmit,
  isUserDetailsLoading = false,
  email = '',
  isOAuthFlow = false,
  onClose,
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  onStepChange: _onStepChange,
  initialValues,
  workDetailsData,
  userDetailsError,
  selectedUserType,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [selectedDialCode, setSelectedDialCode] = useState<string>('+1');
  const selectedIso2 = useMemo(
    () => getIso2FromDialCode(selectedDialCode),
    [selectedDialCode]
  );

  const phoneMask = useMemo(() => getPhoneMask(selectedIso2), [selectedIso2]);

  // When country is not US, allow paste of numbers with spaces by normalizing (same as PhoneStep)
  const handlePhonePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    // eslint-disable-next-line no-unused-vars -- setFieldValue is Formik's setter; param names are for type only
    setFieldValue: (field: string, value: string) => void
  ) => {
    if (selectedIso2 === 'US') return;
    const pasted = e.clipboardData.getData('text');
    const normalized = pasted.replace(/[^\d+]/g, '');
    if (normalized !== pasted) {
      e.preventDefault();
      setFieldValue('phone', normalized);
    }
  };

  const validationSchema = useMemo(
    () =>
      userDetailsValidationSchema.shape({
        phone: Yup.string().when('$isOAuthFlow', {
          is: true,
          then: (schema) =>
            schema
              .required('Phone number is required')
              .test(
                'phone-format',
                'Please enter a valid phone number.',
                (value) => {
                  const v = value || '';
                  const parsed = parsePhoneNumberFromString(
                    v,
                    selectedIso2 as CountryCode
                  );
                  return !!parsed && parsed.isPossible();
                }
              ),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    [selectedIso2]
  );

  useEffect(() => {
    if (userDetailsError) {
      setErrorMessage(userDetailsError);
    }
  }, [userDetailsError]);

  const handleSubmit = async (
    values: RegisterFormValues,
    // eslint-disable-next-line no-unused-vars
    { setSubmitting }: { setSubmitting: (_isSubmitting: boolean) => void }
  ) => {
    try {
      setSubmitting(true);
      if (isOAuthFlow) {
        const e164 = buildE164PhoneNumber(values.phone, selectedDialCode);
        const { exists } = await checkPhoneExist({
          phone_number: e164,
        });
        if (exists) {
          setErrorMessage(ERROR_MESSAGES.phoneNumberAlreadyExists);
          setSubmitting(false);
          return;
        }
        await onUserDetailsSubmit?.({ ...values, phone: e164 });
      } else {
        await onUserDetailsSubmit?.(values);
      }
    } catch (error) {
      const errorMsg =
        (
          error as {
            response?: { data?: { message?: string } };
            message?: string;
          }
        )?.response?.data?.message ||
        (error as { message?: string })?.message ||
        'Please fill in all required fields';
      setErrorMessage(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{
        phone: initialValues?.phone || '',
        code: initialValues?.code || '',
        userType:
          initialValues?.userType ||
          selectedUserType ||
          (ZEME_USERS_ROLE.RENTER as UserType),
        firstName: initialValues?.firstName || '',
        middleName: initialValues?.middleName || '',
        lastName: initialValues?.lastName || '',
        licenseNumber: initialValues?.licenseNumber || '',
        email: initialValues?.email || email || '',
        consent: initialValues?.consent || false,
      }}
      enableReinitialize={true}
      validationSchema={validationSchema}
      context={{ isOAuthFlow }}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        isSubmitting,
        submitForm,
        isValid,
      }) => {
        const handleContinue = () => {
          if (isValid) {
            submitForm();
          }
        };

        return (
          <div className="h-full flex flex-col md:flex-row">
            <div className="md:w-[686px] w-full h-full md:h-[790px] self-stretch px-0 md:px-2.5 pt-0 md:pt-2.5 pb-0 md:pb-8 bg-white rounded-none md:rounded-l-2xl flex flex-col items-start relative">
              {/* Mobile Header */}
              <div className="md:hidden w-full">
                <DialogHeader className="px-5 pt-6 pb-2 flex flex-col gap-3 items-start">
                  <div className="self-stretch flex flex-col gap-4 items-start w-full">
                    <div className="self-stretch inline-flex justify-start items-center gap-2.5">
                      <div className="flex-1 justify-start text-black text-2xl font-medium font-geist leading-9 tracking-[-0.48px]">
                        Tell us about yourself
                      </div>
                      <span
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                      >
                        <Icon
                          iconName="crossicon"
                          iconProps={{ className: 'w-5 h-5 text-gray-600' }}
                        />
                      </span>
                    </div>
                  </div>
                </DialogHeader>
              </div>

              {/* Desktop Header */}
              <div className="hidden md:flex self-stretch flex-col justify-start items-start gap-2">
                <div className="self-stretch h-auto md:h-32 px-8 pt-7 pb-5 bg-white flex flex-col justify-center items-start gap-3">
                  <div className="self-stretch flex flex-col justify-start items-start gap-4">
                    <div className="self-stretch inline-flex justify-start items-center gap-2.5">
                      <div className="flex-1 justify-start text-gray-1000 text-xl md:text-2xl font-medium font-geist leading-7 md:leading-9">
                        Tell us about yourself
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <Form className="flex-1 w-full flex flex-col min-h-0 register-modal-form">
                <div className="flex-1 w-full flex flex-col overflow-y-auto overflow-x-hidden pb-[100px] md:pb-0 min-h-0">
                  {/* Mobile Content */}
                  <div className="md:hidden px-5 py-3 flex flex-col gap-6">
                    {/* Full Name Section */}
                    <div className="flex flex-col gap-[10px] items-start w-full">
                      <div className="flex flex-col gap-2 items-start w-full">
                        <div className="text-gray-800 text-sm font-normal font-geist leading-6 tracking-[-0.14px] w-full">
                          Full Name
                        </div>
                        <div className="w-full">
                          <TextField
                            value={values.firstName}
                            error={touched.firstName && !!errors.firstName}
                            errorMessage={
                              touched.firstName ? errors.firstName : ''
                            }
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="firstName"
                            placeholder="First Name"
                            size="md"
                            variant="light"
                            disabled={isUserDetailsLoading || isSubmitting}
                            inputClassName="!text-gray-1000 !text-sm leading-5"
                            showLabel={false}
                          />
                        </div>
                      </div>
                      <div className="w-full">
                        <TextField
                          value={values.middleName || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="middleName"
                          placeholder="Middle Name"
                          size="md"
                          variant="light"
                          error={touched.middleName && !!errors.middleName}
                          errorMessage={
                            touched.middleName ? errors.middleName : ''
                          }
                          disabled={isUserDetailsLoading || isSubmitting}
                          inputClassName="!text-gray-1000 !text-sm leading-5"
                          showLabel={false}
                        />
                      </div>
                      <div className="w-full">
                        <TextField
                          value={values.lastName}
                          error={touched.lastName && !!errors.lastName}
                          errorMessage={touched.lastName ? errors.lastName : ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="lastName"
                          placeholder="Last Name"
                          size="md"
                          variant="light"
                          disabled={isUserDetailsLoading || isSubmitting}
                          inputClassName="!text-gray-1000 !text-sm leading-5"
                          showLabel={false}
                        />
                      </div>
                    </div>

                    {/* License Number */}
                    {values.userType === ZEME_USERS_ROLE.AGENT && (
                      <div className="flex flex-col gap-2 items-start w-full">
                        <div className="text-gray-800 text-sm font-normal font-geist leading-6 tracking-[-0.14px] w-full">
                          License Number
                        </div>
                        <TextField
                          value={values.licenseNumber || ''}
                          error={
                            touched.licenseNumber && !!errors.licenseNumber
                          }
                          errorMessage={
                            touched.licenseNumber ? errors.licenseNumber : ''
                          }
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="licenseNumber"
                          placeholder="Enter License Number"
                          size="md"
                          variant="light"
                          type="text"
                          disabled={isUserDetailsLoading || isSubmitting}
                          inputClassName="!text-gray-1000 !text-sm leading-5"
                          showLabel={false}
                        />
                      </div>
                    )}

                    {/* Phone (OAuth Flow) or Email */}
                    {isOAuthFlow ? (
                      <div className="flex flex-col gap-2 items-start w-full">
                        <div className="text-gray-800 text-sm font-normal font-geist leading-6 tracking-[-0.14px] w-full">
                          Phone Number
                        </div>
                        <TextField
                          value={values.phone}
                          error={touched.phone && !!errors.phone}
                          errorMessage={touched.phone ? errors.phone : ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          onPaste={(e) => handlePhonePaste(e, setFieldValue)}
                          name="phone"
                          placeholder="Phone Number"
                          mask={phoneMask}
                          size="md"
                          variant="light"
                          type="tel"
                          numericOnly={selectedIso2 !== 'US'}
                          maxLength={selectedIso2 !== 'US' ? 15 : undefined}
                          countryCode={
                            <CountryCodeSelect
                              value={selectedDialCode}
                              onChange={(dialCode) => {
                                setSelectedDialCode(dialCode);
                                setFieldValue(
                                  'phone',
                                  formatPhoneDisplayForCountry(
                                    values.phone,
                                    dialCode
                                  )
                                );
                              }}
                              disabled={isUserDetailsLoading || isSubmitting}
                            />
                          }
                          disabled={isUserDetailsLoading || isSubmitting}
                          inputClassName="!text-gray-1000 !text-sm leading-5"
                          showLabel={false}
                          inputMode="numeric"
                        />
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2 items-start w-full">
                        <div className="text-gray-800 text-sm font-normal font-geist leading-6 tracking-[-0.14px] w-full">
                          Email
                        </div>
                        <TextField
                          value={values.email}
                          error={touched.email && !!errors.email}
                          errorMessage={touched.email ? errors.email : ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="email"
                          placeholder="Enter your email"
                          size="md"
                          variant="light"
                          type="email"
                          disabled={isUserDetailsLoading || isSubmitting}
                          inputClassName="!text-gray-1000 !text-sm leading-5"
                          showLabel={false}
                        />
                      </div>
                    )}
                    {/* Error Message - Mobile */}
                    {errorMessage && (
                      <div className="inline-flex justify-start items-start gap-2">
                        <Icon
                          iconName="warningCircle"
                          iconProps={{
                            className: 'w-5 h-5 flex-shrink-0 mt-0.5',
                          }}
                        />
                        <span className="font-geist font-normal text-[#E5484D] text-[14px] leading-[24px] tracking-[-0.14px]">
                          {errorMessage}
                        </span>
                      </div>
                    )}
                    {/* Checkbox */}
                    <div className="flex flex-col gap-2 items-start w-full">
                      <div className="flex gap-3 items-center justify-center w-full">
                        <Checkbox
                          id="consent"
                          checked={values.consent ?? false}
                          onCheckedChange={(checked) => {
                            handleChange({
                              target: {
                                name: 'consent',
                                value: checked,
                              },
                            });
                          }}
                          name="consent"
                          size="md"
                          variant="primary"
                        />
                        <div className="flex-1 justify-start text-xs text-gray-1000 font-normal font-geist leading-normal tracking-[-0.12px]">
                          By checking this box, you agree to Zeme Inc&apos;s{' '}
                          <Link
                            href={APP_ROUTE.PRIVACY}
                            className="text-focus underline"
                            target="_blank"
                          >
                            Privacy Policy
                          </Link>{' '}
                          and{' '}
                          <Link
                            href={APP_ROUTE.TERMS}
                            className="text-focus underline"
                            target="_blank"
                          >
                            Terms of Service
                          </Link>
                          .
                        </div>
                      </div>
                      {touched.consent && errors.consent && (
                        <div className="inline-flex justify-start items-center gap-2">
                          <Icon
                            iconName="warningCircle"
                            iconProps={{ className: 'w-5 h-5 flex-shrink-0' }}
                          />
                          <span className="font-geist font-normal text-[#E5484D] text-[14px] leading-[24px] tracking-[-0.14px]">
                            Please agree to Zeme Inc&apos;s
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Desktop Content */}
                  <div className="hidden md:flex self-stretch px-8 py-2.5 flex-col justify-start items-end gap-6 md:gap-8">
                    <div className="self-stretch flex flex-col justify-start items-start gap-6">
                      <div className="self-stretch inline-flex justify-center items-start gap-2.5">
                        <div className="flex-1 inline-flex flex-col justify-start items-start gap-2">
                          <div className="self-stretch justify-start text-gray-800 text-sm font-normal font-geist leading-6">
                            Full Name
                          </div>
                          <div className="w-full">
                            <TextField
                              value={values.firstName}
                              error={touched.firstName && !!errors.firstName}
                              errorMessage={
                                touched.firstName ? errors.firstName : ''
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              name="firstName"
                              placeholder="First Name"
                              size="md"
                              variant="light"
                              disabled={isUserDetailsLoading || isSubmitting}
                              inputClassName="!text-gray-1000 text-sm leading-5"
                              showLabel={false}
                            />
                          </div>
                        </div>
                        <div className="flex-1 inline-flex flex-col justify-start items-start gap-2">
                          <div className="self-stretch justify-start text-gray-800 text-sm font-normal font-geist leading-6 opacity-0">
                            &nbsp;
                          </div>
                          <div className="w-full">
                            <TextField
                              value={values.middleName || ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              name="middleName"
                              placeholder="Middle Name"
                              size="md"
                              variant="light"
                              error={touched.middleName && !!errors.middleName}
                              errorMessage={
                                touched.middleName ? errors.middleName : ''
                              }
                              disabled={isUserDetailsLoading || isSubmitting}
                              inputClassName="!text-gray-1000 text-sm leading-5"
                              showLabel={false}
                            />
                          </div>
                        </div>
                        <div className="flex-1 inline-flex flex-col justify-start items-start gap-2">
                          <div className="self-stretch justify-start text-gray-800 text-sm font-normal font-geist leading-6 opacity-0">
                            &nbsp;
                          </div>
                          <div className="w-full">
                            <TextField
                              value={values.lastName}
                              error={touched.lastName && !!errors.lastName}
                              errorMessage={
                                touched.lastName ? errors.lastName : ''
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              name="lastName"
                              placeholder="Last Name"
                              size="md"
                              variant="light"
                              disabled={isUserDetailsLoading || isSubmitting}
                              inputClassName="!text-gray-1000 text-sm leading-5"
                              showLabel={false}
                            />
                          </div>
                        </div>
                      </div>

                      {values.userType === ZEME_USERS_ROLE.AGENT && (
                        <div className="self-stretch inline-flex justify-center items-end gap-2.5">
                          <div className="flex-1 inline-flex flex-col justify-start items-start gap-2">
                            <div className="self-stretch justify-start text-gray-800 text-sm font-normal font-geist leading-6">
                              License Number
                            </div>
                            <TextField
                              value={values.licenseNumber || ''}
                              error={
                                touched.licenseNumber && !!errors.licenseNumber
                              }
                              errorMessage={
                                touched.licenseNumber
                                  ? errors.licenseNumber
                                  : ''
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              name="licenseNumber"
                              placeholder="Enter License Number"
                              size="md"
                              variant="light"
                              type="text"
                              disabled={isUserDetailsLoading || isSubmitting}
                              inputClassName="!text-gray-1000 text-sm leading-5"
                              showLabel={false}
                            />
                          </div>
                        </div>
                      )}

                      {/* Phone (OAuth Flow) or Email */}
                      {isOAuthFlow ? (
                        <div className="self-stretch flex flex-col justify-start items-start gap-2">
                          <div className="self-stretch justify-start text-gray-800 text-sm font-normal font-geist leading-6">
                            Phone Number
                          </div>
                          <TextField
                            value={values.phone}
                            error={touched.phone && !!errors.phone}
                            errorMessage={touched.phone ? errors.phone : ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onPaste={(e) => handlePhonePaste(e, setFieldValue)}
                            name="phone"
                            placeholder="Phone Number"
                            mask={phoneMask}
                            size="md"
                            variant="light"
                            type="tel"
                            numericOnly={selectedIso2 !== 'US'}
                            maxLength={selectedIso2 !== 'US' ? 15 : undefined}
                            countryCode={
                              <CountryCodeSelect
                                value={selectedDialCode}
                                triggerClassName="!text-sm"
                                onChange={(dialCode) => {
                                  setSelectedDialCode(dialCode);
                                  setFieldValue(
                                    'phone',
                                    formatPhoneDisplayForCountry(
                                      values.phone,
                                      dialCode
                                    )
                                  );
                                }}
                                disabled={isUserDetailsLoading || isSubmitting}
                              />
                            }
                            disabled={isUserDetailsLoading || isSubmitting}
                            inputClassName="!text-gray-1000 text-sm leading-5"
                            showLabel={false}
                            inputMode="numeric"
                          />
                        </div>
                      ) : (
                        <div className="self-stretch flex flex-col justify-start items-start gap-2">
                          <div className="self-stretch justify-start text-gray-800 text-sm font-normal font-geist leading-6">
                            Email
                          </div>
                          <TextField
                            value={values.email}
                            error={touched.email && !!errors.email}
                            errorMessage={touched.email ? errors.email : ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="email"
                            placeholder="Enter your email"
                            size="md"
                            variant="light"
                            type="email"
                            disabled={isUserDetailsLoading || isSubmitting}
                            inputClassName="!text-gray-1000 text-sm leading-5"
                            showLabel={false}
                          />
                        </div>
                      )}
                      {/* Error Message - Desktop */}
                      {errorMessage && (
                        <div className="self-stretch inline-flex justify-start items-start gap-2">
                          <Icon
                            iconName="warningCircle"
                            iconProps={{
                              className: 'w-5 h-5 flex-shrink-0 mt-0.5',
                            }}
                          />
                          <span className="font-geist font-normal text-[#E5484D] text-[14px] leading-[24px] tracking-[-0.14px]">
                            {errorMessage}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="self-stretch flex flex-col justify-start items-start gap-2">
                      <div className="self-stretch inline-flex justify-center items-center gap-3">
                        <Checkbox
                          id="consent"
                          checked={values.consent ?? false}
                          onCheckedChange={(checked) => {
                            handleChange({
                              target: {
                                name: 'consent',
                                value: checked,
                              },
                            });
                          }}
                          name="consent"
                          size="md"
                          variant="primary"
                        />
                        <div className="flex-1 justify-start text-xs text-gray-1000 font-normal font-geist">
                          By checking this box, you agree to Zeme Inc&apos;s{' '}
                          <Link
                            href={APP_ROUTE.PRIVACY}
                            className="text-focus underline"
                            target="_blank"
                          >
                            Privacy Policy
                          </Link>{' '}
                          and{' '}
                          <Link
                            href={APP_ROUTE.TERMS}
                            className="text-focus underline"
                            target="_blank"
                          >
                            Terms of Service
                          </Link>
                          .
                        </div>
                      </div>
                      {touched.consent && errors.consent && (
                        <div className="self-stretch inline-flex justify-start items-center gap-2">
                          <Icon
                            iconName="warningCircle"
                            iconProps={{ className: 'w-5 h-5 flex-shrink-0' }}
                          />
                          <span className="font-geist font-normal text-[#E5484D] text-[14px] leading-[24px] tracking-[-0.14px]">
                            Please agree to Zeme Inc&apos;s
                          </span>
                        </div>
                      )}
                    </div>

                    <Button
                      type="submit"
                      variant="primary"
                      size="xl"
                      className="self-stretch h-12 px-4 py-3 bg-focus rounded-[36px] inline-flex justify-center items-center gap-2"
                      disabled={
                        isUserDetailsLoading || isSubmitting || !isValid
                      }
                      isLoading={isUserDetailsLoading || isSubmitting}
                      loadingType="dots"
                    >
                      <div className="justify-start text-white text-base font-medium font-geist leading-7">
                        Continue
                      </div>
                      <Icon
                        iconName="arrowRight"
                        iconProps={{ className: 'size-5 text-white' }}
                      />
                    </Button>
                  </div>
                </div>
              </Form>

              {/* Mobile Bottom Navigation */}
              <div className="md:hidden absolute bottom-0 left-0 right-0 w-full px-5 py-5 bg-white border-b border-gray-400 shadow-[0px_-1px_20px_0px_rgba(0,0,0,0.05)] flex justify-center items-center z-10">
                <Button
                  type="button"
                  onClick={handleContinue}
                  disabled={isUserDetailsLoading || isSubmitting || !isValid}
                  className="flex-1 h-[52px] px-8 py-[10px] bg-focus rounded-xl flex justify-center items-center min-w-0 disabled:opacity-50"
                >
                  <span className="text-white text-base font-semibold font-geist leading-normal tracking-[-0.16px]">
                    Continue
                  </span>
                </Button>
              </div>
            </div>
            <div className="hidden md:flex relative flex-col p-2.5 items-center justify-center gap-2.5 self-stretch shrink-0 rounded-r-2xl bg-background-200 overflow-hidden w-[550px] h-[790px]">
              <GradientPanelSvg
                className="absolute inset-0 w-full h-full"
                preserveAspectRatio="none"
              />
              {/* Center Card Component */}
              <UserProfileCard
                firstName={values.firstName}
                lastName={values.lastName}
                companyName={workDetailsData?.companyName}
                companyPhoto={workDetailsData?.companyPhoto}
                hideCompany={values.userType === ZEME_USERS_ROLE.RENTER}
              />
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

export default UserDetailsStep;
